/**
 * In-memory fixture client matching the `apiRequest` signature.
 *
 * Dev convenience only — guarded by VITE_KB_USE_FIXTURES + the
 * MODE !== "production" gate in fixture-flag.ts. See NFR-Design § 2.
 *
 * Behaviour intentionally minimal: routes the common KB endpoints to
 * an in-memory store so the Knowledge nav is browse-able without a
 * seeded API. The query endpoint always returns `{ chunks: [], no_match: true }`
 * until PR-C is wired in production.
 */

import { AgentsApiError } from "../../../agents/api/client";
import type {
  AgentKnowledgeBaseAttachment,
  AttachKnowledgeBaseRequest,
  CreateKnowledgeBaseRequest,
  CreateSourceRequest,
  FinalizeSourceResponse,
  KbJob,
  KbMetaModel,
  KbQueryRequest,
  KbQueryResponse,
  KnowledgeBase,
  KnowledgeSource,
  Paged,
  UpdateKnowledgeBaseRequest,
  UploadCreateResponse,
} from "../types";

interface Store {
  kbs: KnowledgeBase[];
  sources: Map<string, KnowledgeSource[]>; // kbId -> sources
  jobs: Map<string, KbJob[]>; // kbId -> jobs
  attachments: Map<string, AgentKnowledgeBaseAttachment[]>; // agentId -> attachments
}

const NOW = () => new Date().toISOString();
const UUID = () =>
  globalThis.crypto?.randomUUID?.() ??
  `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, "0")}`;

function seed(): Store {
  const wsId = "00000000-0000-4000-8000-000000000001";
  const kbId = "00000000-0000-4000-8000-0000000000kb";
  const kb: KnowledgeBase = {
    id: kbId,
    workspace_id: wsId,
    name: "Onboarding docs",
    description: "Internal onboarding handbook + product FAQs.",
    status_label: null,
    embedding_model_id: "openai:text-embedding-3-small",
    chunk_strategy: "markdown_aware",
    chunk_size_tokens: 800,
    chunk_overlap_tokens: 100,
    vector_top_n: 25,
    keyword_top_n: 25,
    hybrid_search: false,
    min_rerank_score: 0.3,
    contextual_chunking: false,
    query_rewrite_enabled: false,
    sync_cadence: "off",
    last_sync_at: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    sources_count: 2,
    chunks_count: 64,
    created_at: NOW(),
    updated_at: NOW(),
  };

  const src1: KnowledgeSource = {
    id: "00000000-0000-4000-8000-000000000s01",
    knowledge_base_id: kbId,
    workspace_id: wsId,
    name: "Onboarding Handbook v3",
    source_type: "upload",
    status: "ingested",
    content_type_declared: "application/pdf",
    byte_size_declared: 524288,
    external_url: null,
    last_ingest_run_id: null,
    tags: [],
    metadata: {},
    created_at: NOW(),
    updated_at: NOW(),
    last_error_code: null,
    last_error_message: null,
  };
  const src2: KnowledgeSource = {
    id: "00000000-0000-4000-8000-000000000s02",
    knowledge_base_id: kbId,
    workspace_id: wsId,
    name: "Pricing page",
    source_type: "url",
    status: "failed",
    content_type_declared: null,
    byte_size_declared: null,
    external_url: "https://example.com/pricing",
    last_ingest_run_id: null,
    tags: [],
    metadata: {},
    created_at: NOW(),
    updated_at: NOW(),
    last_error_code: "PARSE_ERROR",
    last_error_message: "pdf-parse failed: bad XRef entry",
  };

  const job1: KbJob = {
    id: "00000000-0000-4000-8000-000000000j01",
    knowledge_base_id: kbId,
    knowledge_source_id: src1.id,
    kind: "ingest",
    state: "completed",
    trigger: "initial",
    started_at: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    ended_at: new Date(Date.now() - 1000 * 60 * 55).toISOString(),
    chunks_count: 32,
    bytes_in: 524288,
    error_code: null,
    error_message: null,
    created_at: NOW(),
  };
  const job2: KbJob = {
    id: "00000000-0000-4000-8000-000000000j02",
    knowledge_base_id: kbId,
    knowledge_source_id: src2.id,
    kind: "ingest",
    state: "failed",
    trigger: "initial",
    started_at: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
    ended_at: new Date(Date.now() - 1000 * 60 * 9).toISOString(),
    chunks_count: null,
    bytes_in: null,
    error_code: "FETCH_HTTP_403",
    error_message: "Server returned 403 Forbidden",
    created_at: NOW(),
  };

  return {
    kbs: [kb],
    sources: new Map([[kbId, [src1, src2]]]),
    jobs: new Map([[kbId, [job1, job2]]]),
    attachments: new Map(),
  };
}

const store = seed();

const MODELS: KbMetaModel[] = [
  {
    id: "openai:text-embedding-3-small",
    provider: "openai",
    dimensions: 1536,
    default_batch_size: 96,
    default_concurrency: 4,
    max_input_tokens: 8191,
    label: "OpenAI · text-embedding-3-small",
    notes: "Default model.",
  },
  {
    id: "openai:text-embedding-3-large",
    provider: "openai",
    dimensions: 3072,
    default_batch_size: 64,
    default_concurrency: 4,
    max_input_tokens: 8191,
    label: "OpenAI · text-embedding-3-large",
    notes: "Higher quality, 3072 dimensions.",
  },
  {
    id: "voyage:voyage-3",
    provider: "voyage",
    dimensions: 1024,
    default_batch_size: 64,
    default_concurrency: 4,
    max_input_tokens: 32000,
    label: "Voyage · voyage-3",
    notes: "Compact retrieval model.",
  },
  {
    id: "voyage:voyage-3-large",
    provider: "voyage",
    dimensions: 1024,
    default_batch_size: 64,
    default_concurrency: 4,
    max_input_tokens: 32000,
    label: "Voyage · voyage-3-large",
    notes: "Flagship Voyage model, 32K context.",
  },
  {
    id: "cohere:embed-english-v3",
    provider: "cohere",
    dimensions: 1024,
    default_batch_size: 64,
    default_concurrency: 4,
    max_input_tokens: 512,
    label: "Cohere · embed-english-v3",
    notes: "English-optimized retrieval model.",
  },
  {
    id: "cohere:embed-v4.0",
    provider: "cohere",
    dimensions: 1536,
    default_batch_size: 64,
    default_concurrency: 4,
    max_input_tokens: 128000,
    label: "Cohere · embed-v4.0",
    notes: "Latest multilingual model, 1536 dims.",
  },
  {
    id: "google:text-embedding-005",
    provider: "google",
    dimensions: 768,
    default_batch_size: 64,
    default_concurrency: 4,
    max_input_tokens: 2048,
    label: "Google · text-embedding-005",
    notes: "Compact Gecko-based model.",
  },
  {
    id: "google:gemini-embedding-001",
    provider: "google",
    dimensions: 3072,
    default_batch_size: 64,
    default_concurrency: 4,
    max_input_tokens: 2048,
    label: "Google · gemini-embedding-001",
    notes: "Highest-quality Google model, 3072 dims (unindexed).",
  },
];

function notFound(message: string): never {
  throw new AgentsApiError({
    status: 404,
    code: "KB_NOT_FOUND",
    message,
    requestId: `fix_${UUID()}`,
  });
}

function paged<T>(items: T[]): Paged<T> {
  return { items, next_cursor: null };
}

function parsePath(path: string): { segments: string[]; query: URLSearchParams } {
  const [pathOnly = "", queryString = ""] = path.split("?");
  return {
    segments: pathOnly.split("/").filter(Boolean),
    query: new URLSearchParams(queryString),
  };
}

export async function fixtureApiRequest<T>(
  method: string,
  path: string,
  body?: unknown,
  search?: Record<string, string | number | undefined>,
): Promise<T> {
  void search;
  const { segments } = parsePath(path);
  // Expected shapes:
  //   api/knowledge-bases
  //   api/knowledge-bases/:kbId
  //   api/knowledge-bases/:kbId/sources[/:sid[/finalize|/sync]]
  //   api/knowledge-bases/:kbId/jobs[/:jobId]
  //   api/knowledge-bases/:kbId/query
  //   api/kb-meta/models
  //   api/agents/:agentId/knowledge-bases[/:kbId]

  if (segments[0] !== "api") notFound(`Fixture: unknown path ${path}`);

  if (segments[1] === "kb-meta" && segments[2] === "models") {
    return { items: MODELS } as T;
  }

  if (segments[1] === "agents" && segments[3] === "knowledge-bases") {
    const agentId = segments[2] ?? "";
    const list = store.attachments.get(agentId) ?? [];
    if (method === "GET") {
      return paged(list) as T;
    }
    if (method === "POST") {
      const req = body as AttachKnowledgeBaseRequest;
      const attached: AgentKnowledgeBaseAttachment = {
        agent_id: agentId,
        knowledge_base_id: req.knowledge_base_id,
        retrieval_mode: req.retrieval_mode,
        top_k: req.top_k ?? 5,
        attached_via: "manual",
        attached_at: NOW(),
      };
      store.attachments.set(agentId, [...list, attached]);
      return attached as T;
    }
    if (method === "DELETE" && segments[4]) {
      const kbId = segments[4];
      store.attachments.set(
        agentId,
        list.filter((a) => a.knowledge_base_id !== kbId),
      );
      return undefined as T;
    }
  }

  if (segments[1] === "knowledge-bases") {
    const kbId = segments[2];

    // /api/knowledge-bases
    if (!kbId) {
      if (method === "GET") return paged(store.kbs) as T;
      if (method === "POST") {
        const req = body as CreateKnowledgeBaseRequest;
        const created: KnowledgeBase = {
          id: UUID(),
          workspace_id: store.kbs[0]?.workspace_id ?? UUID(),
          name: req.name,
          description: req.description ?? null,
          status_label: req.status_label ?? null,
          embedding_model_id: req.embedding_model_id ?? "openai:text-embedding-3-small",
          chunk_strategy: req.chunk_strategy ?? "markdown_aware",
          chunk_size_tokens: req.chunk_size_tokens ?? 800,
          chunk_overlap_tokens: req.chunk_overlap_tokens ?? 100,
          vector_top_n: req.vector_top_n ?? 25,
          keyword_top_n: req.keyword_top_n ?? 25,
          hybrid_search: req.hybrid_search ?? false,
          min_rerank_score: req.min_rerank_score ?? 0.3,
          contextual_chunking: req.contextual_chunking ?? false,
          query_rewrite_enabled: req.query_rewrite_enabled ?? false,
          sync_cadence: req.sync_cadence ?? "off",
          last_sync_at: null,
          sources_count: 0,
          chunks_count: 0,
          created_at: NOW(),
          updated_at: NOW(),
        };
        store.kbs.unshift(created);
        store.sources.set(created.id, []);
        store.jobs.set(created.id, []);
        return created as T;
      }
    }

    const kbIdx = kbId ? store.kbs.findIndex((k) => k.id === kbId) : -1;
    if (kbIdx < 0 && kbId) notFound(`Fixture: KB ${kbId} not found`);
    const kb = store.kbs[kbIdx]!;

    // /api/knowledge-bases/:kbId
    if (kbId && segments.length === 3) {
      if (method === "GET") return kb as T;
      if (method === "PUT") {
        const req = body as UpdateKnowledgeBaseRequest;
        const updated = { ...kb, ...req, updated_at: NOW() } as KnowledgeBase;
        store.kbs[kbIdx] = updated;
        return updated as T;
      }
      if (method === "DELETE") {
        store.kbs.splice(kbIdx, 1);
        store.sources.delete(kbId);
        store.jobs.delete(kbId);
        return undefined as T;
      }
    }

    // /api/knowledge-bases/:kbId/sources[/:sid[/finalize|/sync]]
    if (kbId && segments[3] === "sources") {
      const sources = store.sources.get(kbId) ?? [];
      const sid = segments[4];

      if (!sid && method === "GET") return paged(sources) as T;

      if (!sid && method === "POST") {
        const req = body as CreateSourceRequest;
        const src: KnowledgeSource = {
          id: UUID(),
          knowledge_base_id: kbId,
          workspace_id: kb.workspace_id,
          name: req.name,
          source_type: req.source_type,
          status: req.source_type === "upload" ? "pending_upload" : "ingesting",
          content_type_declared: req.source_type === "upload" ? req.content_type : null,
          byte_size_declared: req.source_type === "upload" ? req.byte_size : null,
          external_url:
            req.source_type === "url" || req.source_type === "sitemap"
              ? req.url
              : req.source_type === "paste"
                ? (req.source_url ?? null)
                : null,
          last_ingest_run_id: null,
          tags: [],
          metadata: {},
          created_at: NOW(),
          updated_at: NOW(),
          last_error_code: null,
          last_error_message: null,
        };
        store.sources.set(kbId, [src, ...sources]);
        store.kbs[kbIdx] = { ...kb, sources_count: kb.sources_count + 1, updated_at: NOW() };

        if (req.source_type === "upload") {
          const resp: UploadCreateResponse = {
            source_id: src.id,
            presigned_put_url: `blob:fixture://${src.id}`,
            expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
            max_bytes: req.byte_size,
          };
          return resp as T;
        }
        const job: KbJob = {
          id: UUID(),
          knowledge_base_id: kbId,
          knowledge_source_id: src.id,
          kind: "ingest",
          state: "queued",
          trigger: "initial",
          started_at: null,
          ended_at: null,
          chunks_count: null,
          bytes_in: null,
          error_code: null,
          error_message: null,
          created_at: NOW(),
        };
        store.jobs.set(kbId, [job, ...(store.jobs.get(kbId) ?? [])]);
        return { source: src, job_id: job.id } as T;
      }

      if (sid && segments[5] === "finalize" && method === "POST") {
        const src = sources.find((s) => s.id === sid);
        if (!src) notFound(`Fixture: source ${sid} not found`);
        src.status = "ingesting";
        src.updated_at = NOW();
        const resp: FinalizeSourceResponse = { source_id: sid, status: "ingesting" };
        return resp as T;
      }

      if (sid && segments[5] === "sync" && method === "POST") {
        const job: KbJob = {
          id: UUID(),
          knowledge_base_id: kbId,
          knowledge_source_id: sid,
          kind: "resync",
          state: "queued",
          trigger: "manual",
          started_at: null,
          ended_at: null,
          chunks_count: null,
          bytes_in: null,
          error_code: null,
          error_message: null,
          created_at: NOW(),
        };
        store.jobs.set(kbId, [job, ...(store.jobs.get(kbId) ?? [])]);
        return job as T;
      }

      if (sid && segments.length === 5 && method === "DELETE") {
        store.sources.set(
          kbId,
          sources.filter((s) => s.id !== sid),
        );
        store.kbs[kbIdx] = {
          ...kb,
          sources_count: Math.max(0, kb.sources_count - 1),
          updated_at: NOW(),
        };
        return undefined as T;
      }
    }

    // /api/knowledge-bases/:kbId/jobs[/:jobId]
    if (kbId && segments[3] === "jobs") {
      const jobs = store.jobs.get(kbId) ?? [];
      const jobId = segments[4];
      if (!jobId && method === "GET") return paged(jobs) as T;
      if (jobId && method === "GET") {
        const j = jobs.find((x) => x.id === jobId);
        if (!j) notFound(`Fixture: job ${jobId} not found`);
        return j as T;
      }
    }

    // /api/knowledge-bases/:kbId/query
    if (kbId && segments[3] === "query" && method === "POST") {
      void (body as KbQueryRequest);
      const resp: KbQueryResponse = {
        chunks: [],
        no_match: true,
        retrieval: {
          embedding_model_id: "text-embedding-3-small",
          chunk_strategy: "markdown_aware",
          candidates_scanned: 0,
          returned: 0,
          min_rerank_score: 0.2,
          hybrid_used: false,
          vector_candidates: 0,
          keyword_candidates: 0,
          reranker_disabled: false,
          reranker_skipped: false,
          reranker_fallback: false,
        },
      };
      return resp as T;
    }
  }

  notFound(`Fixture: ${method} ${path}`);
}
